/*
 * Network Logger utilitario para rastrear requisições e respostas HTTP.
 * Este módulo é focado em depuração de chamadas de API (dev/test),
 * evitando gravação de dados sensíveis em produção.
 */

export type NetworkLevel = 'info' | 'warn' | 'error' | 'debug';

export interface NetworkEntry {
  id: string;
  ts: number;
  level: NetworkLevel;
  endpoint: string;
  method: string;
  url?: string;
  status?: number;
  request?: {
    payload?: unknown;
    headers?: Record<string, string>;
  };
  response?: {
    bodyPreview?: string;
  };
  error?: {
    message: string;
    stack?: string;
  };
}

/**
 * Verifica se o logger de rede está habilitado via variável de ambiente.
 */
export function isNetworkLogEnabled(): boolean {
  const flag = import.meta?.env?.VITE_ENABLE_NETWORK_LOGS;
  return String(flag).toLowerCase() === 'true';
}

/**
 * Classe simples para armazenar entradas de rede em memória com limite.
 */
class NetworkLogger {
  private logs: NetworkEntry[] = [];
  private errors: NetworkEntry[] = [];
  private maxItems = 200;

  /** Gera um ID legível para correlação de eventos */
  private genId(): string {
    const rnd = Math.random().toString(36).slice(2, 8);
    return `net_${Date.now()}_${rnd}`;
  }

  /** Adiciona uma entrada genérica de log */
  add(level: NetworkLevel, entry: Omit<NetworkEntry, 'id' | 'ts' | 'level'>): NetworkEntry {
    if (!isNetworkLogEnabled()) {
      return { id: 'disabled', ts: Date.now(), level, endpoint: entry.endpoint, method: entry.method } as NetworkEntry;
    }
    const item: NetworkEntry = { id: this.genId(), ts: Date.now(), level, ...entry } as NetworkEntry;
    this.logs.push(item);
    if (this.logs.length > this.maxItems) this.logs.shift();
    if (level === 'error') {
      this.errors.push(item);
      if (this.errors.length > this.maxItems) this.errors.shift();
    }
    // Expor globalmente em dev para inspeção rápida
    if (typeof window !== 'undefined') {
      const janelaDebug = window as unknown as Record<string, unknown>;
      janelaDebug.__networkLogs = this.logs as unknown as NetworkEntry[];
      janelaDebug.__networkErrors = this.errors as unknown as NetworkEntry[];
    }
    return item;
  }

  /** Obtém todos os logs */
  getLogs(): NetworkEntry[] {
    return [...this.logs];
  }

  /** Obtém apenas erros */
  getErrors(): NetworkEntry[] {
    return [...this.errors];
  }
}

export const networkLogger = new NetworkLogger();

/**
 * Loga a requisição (payload e headers truncados) para um endpoint.
 */
export function logNetworkRequest(args: { endpoint: string; method: string; url?: string; payload?: unknown; headers?: Record<string, string> }) {
  if (!isNetworkLogEnabled()) return;
  networkLogger.add('info', {
    endpoint: args.endpoint,
    method: args.method,
    url: args.url,
    request: {
      payload: args.payload,
      headers: args.headers,
    },
  });
}

/**
 * Loga a resposta (body truncado) para um endpoint.
 * Útil para confirmar estruturas como data.data/options sem poluir o console.
 */
export function logNetworkResponse(args: { endpoint: string; method: string; url?: string; status?: number; body?: unknown }) {
  if (!isNetworkLogEnabled()) return;
  let preview: string = '';
  try {
    const serialized = typeof args.body === 'string' ? args.body : JSON.stringify(args.body);
    preview = (serialized || '').slice(0, 2000);
  } catch {
    preview = String(args.body ?? '');
  }
  networkLogger.add('info', {
    endpoint: args.endpoint,
    method: args.method,
    url: args.url,
    status: args.status,
    response: { bodyPreview: preview },
  });
}

/**
 * Loga um erro de rede com mensagem e status (quando disponível).
 */
export function logNetworkError(args: { endpoint: string; method: string; url?: string; status?: number; error: unknown }) {
  if (!isNetworkLogEnabled()) return;
  let message = 'unknown-error';
  let stack: string | undefined;
  if (args.error instanceof Error) {
    message = args.error.message;
    stack = args.error.stack;
  } else {
    try {
      message = JSON.stringify(args.error);
    } catch {
      message = String(args.error);
    }
  }
  networkLogger.add('error', {
    endpoint: args.endpoint,
    method: args.method,
    url: args.url,
    status: args.status,
    error: { message, stack },
  });
}

/**
 * Interface pública para recuperar logs e erros.
 */
export function getNetworkLogs(): NetworkEntry[] {
  return networkLogger.getLogs();
}

export function getNetworkErrors(): NetworkEntry[] {
  return networkLogger.getErrors();
}
